"use client";

import { useState } from "react";
import {
	type Layout,
	ResponsiveGridLayout,
	type ResponsiveLayouts,
	useContainerWidth,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { useIsomorphicLayoutEffect } from "#/hooks/common/useIsomorphicLayoutEffect.ts";
import { AccountInfo } from "./account-info/account-info.tsx";
import { MarketInfo } from "./market-info/market-info.tsx";
import { OrderForm } from "./order-form/order-form.tsx";
import { Orderbook } from "./orderbook/orderbook.tsx";
import { Positions } from "./positions/positions.tsx";
import { Chart } from "./trade-chart/chart.tsx";

const STORAGE_KEY = `trade-layout`;

function loadLayouts(): ResponsiveLayouts | null {
	if (typeof window === "undefined") return null;
	const saved = localStorage.getItem(STORAGE_KEY);
	if (!saved) return null;
	return JSON.parse(saved) as ResponsiveLayouts;
}

function saveLayouts(_layout: Layout, allLayouts: ResponsiveLayouts) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(allLayouts));
}

const HEADER_H = 48;
const GAP = 6;
const PAD_Y = 6;
const VIEWPORT_ROWS = 24;

const defaultLayouts = {
	lg: [
		{ i: "market-info", x: 0, y: 0, w: 16, h: 2, minH: 2 },
		{ i: "chart", x: 0, y: 2, w: 9, h: 16, minH: 16 },
		{ i: "orderbook", x: 9, y: 2, w: 3, h: 16, minH: 16 },
		{ i: "order-form", x: 12, y: 2, w: 4, h: 16, minH: 16 },
		{ i: "positions", x: 0, y: 16, w: 12, h: 12, minH: 12 },
		{ i: "account-info", x: 12, y: 16, w: 4, h: 12, minH: 12 },
	],
	md: [
		{ i: "market-info", x: 0, y: 0, w: 12, h: 2, minH: 2 },
		{ i: "chart", x: 0, y: 2, w: 8, h: 14, minH: 14 },
		{ i: "orderbook", x: 8, y: 2, w: 4, h: 14, minH: 14 },
		{ i: "order-form", x: 6, y: 16, w: 6, h: 14, minH: 14 },
		{ i: "account-info", x: 0, y: 16, w: 6, h: 14, minH: 14 },
		{ i: "positions", x: 0, y: 24, w: 12, h: 10, minH: 10 },
	],
	sm: [
		{ i: "market-info", x: 0, y: 0, w: 8, h: 2, minH: 2 },
		{ i: "chart", x: 0, y: 3, w: 8, h: 14, minH: 14 },
		{ i: "order-form", x: 4, y: 17, w: 4, h: 14, minH: 14 },
		{ i: "orderbook", x: 0, y: 17, w: 4, h: 14, minH: 14 },
		{ i: "account-info", x: 0, y: 31, w: 8, h: 12, minH: 12 },
		{ i: "positions", x: 0, y: 38, w: 8, h: 8, minH: 8 },
	],
	xs: [
		{ i: "market-info", x: 0, y: 0, w: 4, h: 2, minH: 2 },
		{ i: "chart", x: 0, y: 4, w: 4, h: 14, minH: 14 },
		{ i: "orderbook", x: 0, y: 32, w: 4, h: 14, minH: 14 },
		{ i: "order-form", x: 0, y: 18, w: 4, h: 20, minH: 20 },
		{ i: "account-info", x: 0, y: 40, w: 4, h: 14, minH: 14 },
		{ i: "positions", x: 0, y: 48, w: 4, h: 12, minH: 12 },
	],
	xxs: [
		{ i: "market-info", x: 0, y: 0, w: 2, h: 2, minH: 2 },
		{ i: "chart", x: 0, y: 5, w: 2, h: 16, minH: 16 },
		{ i: "orderbook", x: 0, y: 37, w: 2, h: 16, minH: 16 },
		{ i: "order-form", x: 0, y: 21, w: 2, h: 20, minH: 20 },
		{ i: "account-info", x: 0, y: 46, w: 2, h: 14, minH: 14 },
		{ i: "positions", x: 0, y: 55, w: 2, h: 12, minH: 12 },
	],
};

const desktopComponents: Record<string, React.FC> = {
	"market-info": MarketInfo,
	chart: Chart,
	orderbook: Orderbook,
	"order-form": OrderForm,
	"account-info": AccountInfo,
	positions: Positions,
};

function computeRowHeight(viewportH: number) {
	const available = viewportH - HEADER_H - PAD_Y * 2;
	const totalGaps = (VIEWPORT_ROWS - 1) * GAP;
	const computed = (available - totalGaps) / VIEWPORT_ROWS;
	return Math.max(computed, 20);
}

export function DesktopTradeLayout() {
	const { width, containerRef, mounted } = useContainerWidth({
		measureBeforeMount: true,
	});
	const [viewportH, setViewportH] = useState(
		typeof window !== "undefined" ? window.innerHeight : 900,
	);

	useIsomorphicLayoutEffect(() => {
		const onResize = () => setViewportH(window.innerHeight);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const [layouts, setLayouts] = useState(() => loadLayouts() ?? defaultLayouts);
	const rowHeight = computeRowHeight(viewportH);

	function handleLayoutChange(layout: Layout, allLayouts: ResponsiveLayouts) {
		const merged = { ...defaultLayouts, ...allLayouts };
		setLayouts(merged);
		saveLayouts(layout, merged);
	}

	function handleBreakpointChange(bp: string) {
		if (bp === "lg" || bp === "md") {
			setViewportH(window.innerHeight);
		}
	}

	return (
		<div ref={containerRef} className="min-h-[calc(100dvh-48px)] w-full py-1.5">
			{mounted && (
				<ResponsiveGridLayout
					className="trade-grid"
					width={width}
					layouts={layouts}
					breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
					cols={{ lg: 16, md: 12, sm: 8, xs: 4, xxs: 2 }}
					rowHeight={rowHeight}
					margin={[GAP, GAP]}
					containerPadding={[0, 0]}
					dragConfig={{ enabled: false }}
					onLayoutChange={handleLayoutChange}
					onBreakpointChange={handleBreakpointChange}
				>
					{Object.entries(desktopComponents).map(([key, Component]) => (
						<div key={key} className="min-h-0">
							<Component />
						</div>
					))}
				</ResponsiveGridLayout>
			)}
		</div>
	);
}
